import React from "react";
import styled from "styled-components";
import * as Sentry from "@sentry/browser";

import { getAppVersion } from "@kubera/common";
import BadgeTip from "components/tooltip/BadgeTip";

const Container = styled.div`
  display: flex;
`;

const MainImageContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${props => props.size / 16}rem;
  height: ${props => props.size / 16}rem;
  box-shadow: ${props => `0 0 0 ${(props.borderSize || 3) / 16}rem ${props.theme.userImagePlaceholderBG}`};
  background: ${props =>
    props.isPortfolioImage ? props.theme.userImagePlaceholderColor : props.theme.userImagePlaceholderBG};
  border-radius: 50%;
  z-index: 10;
`;

const ProfileImage = styled.img`
  position: absolute;
  object-fit: cover;
  display: flex;
  width: ${props => props.size / 16}rem;
  height: ${props => props.size / 16}rem;
  border-radius: 50%;
  background: ${props => props.theme.userImagePlaceholderBG};
`;

const UserInitial = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: ${props => props.size / 16}rem;
  height: ${props => props.size / 16}rem;
  font-family: PT Serif Caption;
  font-size: ${props => props.size / 2 / 16}rem;
  font-style: normal;
  font-weight: 400;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: ${props => (props.isPortfolioImage ? "-0.125rem" : "0")};
  color: ${props =>
    props.isPortfolioImage ? props.theme.userImagePlaceholderBG : props.theme.userImagePlaceholderColor};
`;

const SecondaryImageContainer = styled(MainImageContainer)`
  margin-left: -${props => props.size / 4 / 16}rem;
  z-index: 1;
`;

const Badge = styled(BadgeTip)`
  position: absolute;
  bottom: -0.25rem;
  left: -0.25rem;
  width: 0.5rem;
  height: 0.5rem;
`;

// Retry loading image on error make calls until exponential
const retryLoadOnTarget = (target, imageUrl, timeout = 0) => {
  if (process.env.REACT_APP_ENV === "local") return;

  if (timeout > 48000) {
    Sentry.withScope(scope => {
      scope.setTag("global_error", "ProfileImgError");
      scope.setExtras({
        appVersion: getAppVersion()
      });
      Sentry.captureMessage("Profile image failed to load after retries");
    });
    return;
  }
  setTimeout(() => {
    const img = new Image();
    img.src = `${imageUrl}?t=${new Date().getTime()}`;
    img.onload = () => {
      target.style.display = "flex";
      target.src = `${imageUrl}?t=${new Date().getTime()}`;
    };
    img.onerror = () => {
      retryLoadOnTarget(target, imageUrl, timeout * 2 || 3000);
    };
  }, timeout);
};

const UserProfileImage = ({
  className,
  imageSize,
  userName,
  imageUrl,
  secondaryUserName,
  secondaryImageUrl,
  borderSize,
  showBadge,
  isPortfolioImage = false,
  onClick = () => null,
  ...other
}) => {
  const handleImageError = e => {
    e.target.style.display = "none";

    retryLoadOnTarget(e.target, imageUrl);
  };

  const handleSecondaryImageError = e => {
    e.target.style.display = "none";

    retryLoadOnTarget(e.target, imageUrl);
  };

  return (
    <Container {...other} className={className} data-private data-cy="profileImage" onClick={onClick}>
      <MainImageContainer size={imageSize} borderSize={borderSize} isPortfolioImage={isPortfolioImage}>
        <UserInitial size={imageSize} isPortfolioImage={isPortfolioImage}>
          {userName.substring(0, 1).toLowerCase()}
        </UserInitial>
        {!imageUrl === false && <ProfileImage src={imageUrl} size={imageSize} onError={handleImageError} />}
        {showBadge && <Badge />}
      </MainImageContainer>
      {!secondaryUserName === false && (
        <SecondaryImageContainer size={imageSize} borderSize={borderSize}>
          <UserInitial size={imageSize} isPortfolioImage={isPortfolioImage}>
            {secondaryUserName.substring(0, 1).toLowerCase()}
          </UserInitial>
          {!secondaryImageUrl === false && (
            <ProfileImage src={secondaryImageUrl} size={imageSize} onError={handleSecondaryImageError} />
          )}
        </SecondaryImageContainer>
      )}
    </Container>
  );
};

export default UserProfileImage;
